import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";

interface LoadingSpinnerProps {
  size?: "sm" | "md" | "lg";
  className?: string;
}

export function LoadingSpinner({
  size = "md",
  className,
}: LoadingSpinnerProps) {
  const sizeClasses = {
    sm: "h-4 w-4",
    md: "h-8 w-8",
    lg: "h-12 w-12",
  };

  return (
    <Loader2
      className={cn(
        "animate-spin text-orange-500",
        sizeClasses[size],
        className
      )}
    />
  );
}

interface LoadingStateProps {
  message?: string;
  fullScreen?: boolean;
  size?: "sm" | "md" | "lg";
}

export function LoadingState({
  message = "로딩 중...",
  fullScreen = false,
  size = "md",
}: LoadingStateProps) {
  const content = (
    <div className="flex flex-col items-center justify-center gap-3">
      <LoadingSpinner size={size} />
      {message && <p className="text-sm text-gray-500">{message}</p>}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        {content}
      </div>
    );
  }

  return content;
}

interface LoadingOverlayProps {
  message?: string;
  transparent?: boolean;
}

export function LoadingOverlay({
  message = "로딩 중...",
  transparent = false,
}: LoadingOverlayProps) {
  return (
    <div
      className={cn(
        "absolute inset-0 z-50 flex items-center justify-center",
        transparent ? "bg-white/50 backdrop-blur-sm" : "bg-white"
      )}
    >
      <div className="flex flex-col items-center justify-center gap-3">
        <LoadingSpinner size="lg" />
        {message && <p className="text-sm text-gray-500">{message}</p>}
      </div>
    </div>
  );
}

// Table skeleton loader
export function TableSkeleton({
  rows = 5,
  columns = 5,
}: {
  rows?: number;
  columns?: number;
}) {
  return (
    <div className="w-full space-y-3">
      {/* Header */}
      <div className="flex gap-4">
        {Array.from({ length: columns }).map((_, i) => (
          <div
            key={i}
            className="h-8 flex-1 animate-pulse rounded bg-gray-100"
          />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex gap-4">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <div
              key={colIndex}
              className="h-12 flex-1 animate-pulse rounded bg-gray-50"
            />
          ))}
        </div>
      ))}
    </div>
  );
}

// Card skeleton loader
export function CardSkeleton() {
  return (
    <div className="space-y-3 rounded-lg border border-gray-200 p-6">
      <div className="h-6 w-1/3 animate-pulse rounded bg-gray-100" />
      <div className="h-4 w-2/3 animate-pulse rounded bg-gray-50" />
      <div className="h-4 w-1/2 animate-pulse rounded bg-gray-50" />
    </div>
  );
}

// Product card skeleton loader
export function ProductCardSkeleton() {
  return (
    <div className="space-y-3 rounded-lg border border-gray-200 overflow-hidden">
      <div className="aspect-4/3 animate-pulse bg-gray-100" />
      <div className="p-4 space-y-3">
        <div className="h-5 w-3/4 animate-pulse rounded bg-gray-100" />
        <div className="h-4 w-1/2 animate-pulse rounded bg-gray-50" />
        <div className="flex items-center justify-between">
          <div className="h-6 w-1/3 animate-pulse rounded bg-gray-100" />
          <div className="h-4 w-1/4 animate-pulse rounded bg-gray-50" />
        </div>
      </div>
    </div>
  );
}
